/**
 * AddCxP.js — Formulario nueva Factura CxP
 * UI idéntico a AddInvoices: invoice-container (2fr 1fr), invoice-details (izq),
 * filial-details (der), invoice-footer (bottom full-width).
 * CSS: addInvoice.module.css (mismas clases que AddInvoices).
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../invoices/styles/addInvoice.module.css";

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

function conceptoAuto(nombreGasto, fechaEmision) {
  if (!nombreGasto) return "";
  let month, year;
  if (fechaEmision) {
    [year, month] = fechaEmision.split("-").map(Number); month -= 1;
  } else { const d = new Date(); month = d.getMonth(); year = d.getFullYear(); }
  return `${nombreGasto} — ${MESES[month]} ${year}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr); d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

const fmt = (n) => Number(n || 0).toLocaleString("es-CR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

export default function AddCxP() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    fecha_emision:           today,
    fecha_vencimiento:       today,
    concepto:                "",
    monto:                   "",
    numero_doc_proveedor:    "",
    cuenta_proveedor:        null,
    cuenta_proveedor_nombre: "",
    cuenta_gasto:            null,
    cuenta_gasto_nombre:     "",
  });

  const [cuentasPasivo,  setCuentasPasivo]  = useState([]);
  const [cuentasGasto,   setCuentasGasto]   = useState([]);
  const [proximoNumero,  setProximoNumero]  = useState("CP-…");
  const [errors,        setErrors]        = useState({});
  const [saving,        setSaving]        = useState(false);
  const [apiErr,        setApiErr]        = useState("");
  const [resultado,     setResultado]     = useState(null);

  useEffect(() => {
    apiFetch("/api/finance/cxp/proximo-numero/")
      .then(r => r.json())
      .then(d => setProximoNumero(d.proximo ?? "CP-…"))
      .catch(() => {});

    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&padre_nombre=Cuentas+por+Pagar+Locales")
      .then(r => r.json())
      .then(d => setCuentasPasivo(Array.isArray(d) ? d : (d.results ?? [])));

    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasGasto(list);
        if (list.length > 0) {
          const p = list[0];
          setForm(prev => ({ ...prev,
            cuenta_gasto: p.id, cuenta_gasto_nombre: p.nombre,
            concepto: conceptoAuto(p.nombre, prev.fecha_emision),
          }));
        }
      });
  }, []);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  function handleFechaEmision(fecha) {
    setForm(p => ({
      ...p, fecha_emision: fecha,
      fecha_vencimiento: fecha ?? "",
      concepto: conceptoAuto(p.cuenta_gasto_nombre, fecha),
    }));
  }

  function handleCuentaGasto(e) {
    const id = Number(e.target.value);
    const obj = cuentasGasto.find(c => c.id === id);
    setForm(p => ({
      ...p, cuenta_gasto: id || null, cuenta_gasto_nombre: obj?.nombre ?? "",
      concepto: conceptoAuto(obj?.nombre ?? "", p.fecha_emision),
    }));
  }

  function handleCuentaProveedor(e) {
    const id = Number(e.target.value);
    const obj = cuentasPasivo.find(c => c.id === id);
    setForm(p => ({ ...p, cuenta_proveedor: id || null, cuenta_proveedor_nombre: obj?.nombre ?? "" }));
  }

  function validate() {
    const e = {};
    if (!form.numero_doc_proveedor.trim()) e.numero_doc_proveedor = "El N° Doc Proveedor es requerido";
    if (!form.cuenta_proveedor)            e.cuenta_proveedor     = "Seleccioná el proveedor";
    if (!form.cuenta_gasto)                e.cuenta_gasto         = "Seleccioná el tipo de gasto";
    if (!form.concepto.trim())             e.concepto             = "El concepto es requerido";
    if (!form.fecha_emision)               e.fecha_emision        = "La fecha de emisión es requerida";
    if (!form.fecha_vencimiento)           e.fecha_vencimiento    = "La fecha de vencimiento es requerida";
    if (form.fecha_emision && form.fecha_vencimiento &&
        form.fecha_vencimiento < form.fecha_emision)
                                           e.fecha_vencimiento    = "Debe ser posterior a la fecha de emisión";
    if (!form.monto || Number(form.monto) <= 0) e.monto           = "El monto debe ser mayor a 0";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setApiErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiFetch("/api/finance/cxp/", {
        method: "POST",
        body: JSON.stringify({
          fecha_emision:        form.fecha_emision,
          fecha_vencimiento:    form.fecha_vencimiento,
          concepto:             form.concepto.trim(),
          monto:                Number(form.monto),
          numero_doc_proveedor: form.numero_doc_proveedor.trim(),
          cuenta_proveedor:     form.cuenta_proveedor,
          cuenta_gasto:         form.cuenta_gasto,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      setResultado(await res.json());
      setErrors({});
      // Refresh next number for next invoice
      apiFetch("/api/finance/cxp/proximo-numero/")
        .then(r => r.json()).then(d => setProximoNumero(d.proximo ?? "CP-…"));
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  function handleReset(e) {
    e.preventDefault();
    const autoConcepto = conceptoAuto(form.cuenta_gasto_nombre, today);
    setForm({ fecha_emision: today, fecha_vencimiento: addDays(today, 30),
      concepto: autoConcepto, monto: "", numero_doc_proveedor: "",
      cuenta_proveedor: null, cuenta_proveedor_nombre: "",
      cuenta_gasto: form.cuenta_gasto, cuenta_gasto_nombre: form.cuenta_gasto_nombre,
    });
    setErrors({}); setApiErr("");
  }

  // ── Pantalla éxito ──────────────────────────────────────────────────────
  if (resultado) {
    return (
      <>
        <span><Link to="/">Dashboard</Link> / <Link to="/finanzas/cxp/facturas">Cuentas por Pagar</Link> / <b>Registrada</b></span>
        <h2>Generar Documento</h2>
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>
            Factura {resultado.numero_factura} registrada correctamente.
          </span>
          <button onClick={() => setResultado(null)}
            style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer" }}>✕</button>
        </div>
        <div className={styles["invoice-container"]}>
          <div className={styles["invoice-details"]} style={{ padding:32, textAlign:"center" }}>
            <div style={{ fontSize:"2.5rem", marginBottom:12 }}>✅</div>
            <h2 style={{ fontSize:16, fontWeight:700, color:"#e5ecf5", margin:"0 0 4px" }}>{resultado.numero_factura}</h2>
            <p style={{ color:"#8d98a7", fontSize:14, margin:"0 0 20px" }}>Factura CxP registrada correctamente</p>
            <div style={{ background:"rgba(74,222,128,0.07)", border:"1px solid rgba(74,222,128,0.25)",
              borderRadius:6, padding:"12px 16px", textAlign:"left", marginBottom:20 }}>
              <div style={{ color:"#4ade80", fontWeight:600, fontSize:14, marginBottom:6 }}>
                <i className="fa-solid fa-book-open" style={{ marginRight:6 }}/> Asiento de diario generado
              </div>
              <div style={{ color:"#8d98a7", fontSize:13, lineHeight:1.8, fontFamily:"monospace" }}>
                <span style={{ color:"#f87171" }}>DEBE</span>{"  "}{form.cuenta_gasto_nombre}{"  "}₡{fmt(form.monto)}<br/>
                <span style={{ color:"#60a5fa" }}>HABER</span>{"  "}{form.cuenta_proveedor_nombre}{"  "}₡{fmt(form.monto)}
              </div>
            </div>
          </div>
          <div className={styles["filial-details"]} style={{ display:"flex", flexDirection:"column", gap:10, justifyContent:"center", alignItems:"center" }}>
            <button onClick={() => { setResultado(null); handleReset({ preventDefault:()=>{} }); }}
              className={`${styles.btn} ${styles["btn-clean-form"]}`} style={{ width:"80%" }}>
              Nueva Factura
            </button>
            <button onClick={() => navigate("/finanzas/cxp/facturas")}
              className={`${styles.btn} ${styles["btn-save"]}`} style={{ width:"80%" }}>
              Ver CxP
            </button>
          </div>
          <div className={styles["invoice-footer"]}/>
        </div>
      </>
    );
  }

  const errorCount = Object.keys(errors).length;
  const montoNum = Number(form.monto) || 0;

  return (
    <>
      {/* Breadcrumb */}
      
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={styles.inactive}> Cuentas por Pagar /</span>
        </Link>
        <Link to="/finanzas/cxp/facturas">
          <span className={styles.inactive}> Facturas /</span>
        </Link>
        <span className={styles.active}> Agregar</span>
      </span>
          
        
      <h2 className="mt-2 me-2 p-0 title">Generar Factura</h2>

      {/* Error count banner */}
      {errorCount > 0 && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8,
            color:"#f87171", fontWeight:600, marginBottom:6 }}>
            <i className="fa-solid fa-triangle-exclamation"/>
            Hay {errorCount} campo{errorCount > 1 ? "s" : ""} con error
          </div>
          <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
            {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>
      )}

      {/* API error */}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16,
          color:"#f87171", fontSize:"0.88rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      {/* invoice-container — mismo grid que AddInvoices */}
      <form onSubmit={handleSubmit}>
        <div className={styles["invoice-container"]}>

          {/* ── Izquierda: Detalles del Documento ── */}
          <div className={styles["invoice-details"]}>
            <div className={styles["invoice-details-header"]}>
              <h1>
                <i className={`fa-solid fa-file-invoice-dollar ${styles["icon-invoice-details"]}`}/>
                Detalles del Documento
              </h1>
            </div>
            <div className={styles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* N° Doc Proveedor + consecutivo automático debajo */}
              <div>
                <label>N° Doc Proveedor <span style={{ color:"#f87171" }}>*</span></label>
                <input className="form-control" type="text"
                  placeholder="F-00000"
                  value={form.numero_doc_proveedor}
                  onChange={e => set("numero_doc_proveedor", e.target.value)}
                  style={errors.numero_doc_proveedor ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.numero_doc_proveedor}/>
                <div style={{ fontSize:"0.73rem", color:"#6b7280", marginTop:3,
                  display:"flex", alignItems:"center", gap:5 }}>
                  <i className="fa-solid fa-hashtag" style={{ fontSize:"0.65rem" }}/>
                  N° interno asignado automáticamente al guardar:
                  <span style={{ fontFamily:"monospace", color:"#f97316",
                    fontWeight:600 }}>{proximoNumero}</span>
                </div>
              </div>

              {/* Proveedor (HABER) */}
              <div>
                <label>Proveedor <span style={{ color:"#f87171" }}>*</span></label>
                <select className="form-select"
                  value={form.cuenta_proveedor ?? ""}
                  onChange={handleCuentaProveedor}
                  style={errors.cuenta_proveedor ? { borderColor:"#ef4444" } : undefined}>
                  <option value="">Nombre del proveedor</option>
                  {cuentasPasivo.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_proveedor}/>
              </div>

              {/* Gasto (DEBE) */}
              <div>
                <label>Gasto <span style={{ color:"#f87171" }}>*</span></label>
                <select className="form-select"
                  value={form.cuenta_gasto ?? ""}
                  onChange={handleCuentaGasto}
                  style={errors.cuenta_gasto ? { borderColor:"#ef4444" } : undefined}>
                  <option value="">Seleccione un tipo de gasto</option>
                  {cuentasGasto.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_gasto}/>
              </div>

              {/* Concepto (auto, editable) */}
              <div>
                <label style={{ display:"flex", alignItems:"center", gap:6 }}>
                  Concepto
                  <span style={{ fontSize:"0.68rem", color:"#9ca3af", background:"#1f2937",
                    border:"1px solid #374151", borderRadius:4, padding:"1px 6px", fontWeight:400 }}>
                    auto
                  </span>
                </label>
                <input className="form-control"
                  placeholder="Descripción del documento…"
                  value={form.concepto}
                  onChange={e => set("concepto", e.target.value)}
                  style={errors.concepto ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.concepto}/>
                <div style={{ fontSize:"0.73rem", color:"#6b7280", marginTop:3 }}>
                  Podés editarlo manualmente.
                </div>
              </div>

              {/* Fechas */}
              <div className="row">
                <div className="col">
                  <label>Fecha de emisión <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha_emision}
                    onChange={e => handleFechaEmision(e.target.value)}
                    style={errors.fecha_emision ? { borderColor:"#ef4444" } : undefined}/>
                  <FieldError msg={errors.fecha_emision}/>
                </div>
                <div className="col">
                  <label>Fecha vencimiento <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha_vencimiento}
                    onChange={e => set("fecha_vencimiento", e.target.value)}
                    style={errors.fecha_vencimiento ? { borderColor:"#ef4444" } : undefined}/>
                  <FieldError msg={errors.fecha_vencimiento}/>
                </div>
              </div>

              {/* Monto */}
              <div>
                <label>Monto <span style={{ color:"#f87171" }}>*</span></label>
                <input type="number" className="form-control"
                  placeholder="0.00" min="0.01" step="0.01"
                  value={form.monto}
                  onChange={e => set("monto", e.target.value)}
                  style={errors.monto ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.monto}/>
              </div>

            </div>
          </div>

          {/* ── Derecha: Resumen (igual que FilialDetails) ── */}
          <div className={styles["filial-details"]}>
            <div className={styles["filial-details-header"]}>
              <h1>
                <i className={`fa-solid fa-house ${styles["icon-filial-details"]}`}/>
                Resumen del Gasto
              </h1>
            </div>

            <div className={styles["invoice-details-body"]} style={{ marginTop:14 }}>
              {/* Resumen rápido */}
              <div className={styles["filial-details-summary"]}>
                <div className={styles["filial-summary-relevant"]}>
                  <h2>RESUMEN RÁPIDO</h2>
                  {[
                    ["N° Doc Proveedor", form.numero_doc_proveedor || "—"],
                    ["Proveedor",        form.cuenta_proveedor_nombre || "—"],
                    ["Gasto",            form.cuenta_gasto_nombre || "—"],
                    ["Fecha de emisión", form.fecha_emision || "—"],
                    ["Fecha vencimiento",form.fecha_vencimiento || "—"],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display:"flex", justifyContent:"space-between",
                      marginBottom:4, fontSize:14 }}>
                      <span style={{ color:"#6b7280" }}>{label}</span>
                      <span style={{ color:"rgba(141,152,167,0.7)", maxWidth:130,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap" }}>{val}</span>
                    </div>
                  ))}
                </div>
                <div className={styles["filial-summary-important"]} style={{ marginTop:12 }}>
                  <h2>Total a pagar</h2>
                  <span>₡{fmt(montoNum)}</span>
                </div>
              </div>

            </div>
          </div>

          {/* ── Footer — full width (igual que InvoiceFooter) ── */}
          <div className={styles["invoice-footer"]}>
            <div className={styles.actions}>
              <button type="button" onClick={handleReset}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}>
                <i className="fa-solid fa-rotate-left"/> Limpiar Formulario
              </button>
              <button type="submit" disabled={saving}
                className={`${styles.btn} ${styles["btn-save"]}`}
                style={{ opacity: saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> Guardando…</>
                  : <><i className="fa-solid fa-lock"/> Guardar</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}
